import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect } from 'react'
import { ticketingService } from '../services/ticketing.service'
import type { EventDocument, TicketTypeDocument } from '@/types'

export function useEvent(slug: string) {
  const [event, setEvent] = useState<(EventDocument & { id: string }) | null>(null)
  const [ticketTypes, setTicketTypes] = useState<Array<TicketTypeDocument & { id: string }>>([])
  const [sharedRemainingSeats, setSharedRemainingSeats] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setError(null)
    setSharedRemainingSeats(null)
    ticketingService
      .getEventBySlug(slug)
      .then(async (ev) => {
        if (!ev) {
          setError(withErrorCode('Event not found', 'app/validation'))
          return
        }
        setEvent(ev)
        if (ev.publicSalesEnabled === false || (ev.productionId && ev.publicSalesEnabled !== true)) {
          setTicketTypes([])
          setSharedRemainingSeats(null)
          return
        }
        const [types, remainingSeats] = await Promise.all([
          ticketingService.getTicketTypes(ev.id),
          ev.capacity === undefined
            ? Promise.resolve<number | null>(null)
            : ticketingService.getPublicRemainingSeats(ev.id),
        ])
        setTicketTypes(types)
        setSharedRemainingSeats(remainingSeats)
      })
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load event')),
      )
      .finally(() => setLoading(false))
  }, [slug])

  return { event, ticketTypes, sharedRemainingSeats, loading, error }
}
